"use client";

import {
  createContext,
  useContext,
  type ReactNode,
} from "react";
import type { AssignSituationStrategy } from "@/generated/fastapi";
import useConfirmationDialog from "@/shared/ui/useConfirmationDialog";
import type { ConfirmationDialogController } from "@/shared/ui/useConfirmationDialog";
import type { RelatedThoughtsDeleteChoice } from "./StudioDeletionStateProvider";

export type DeleteSelectionConfirmation = {
  deleteThoughts: boolean;
  deleteSituations: boolean;
  relatedThoughtsChoice: RelatedThoughtsDeleteChoice;
};

type StudioConfirmationContextValue = {
  confirmSelectedThoughtsDeletion: ConfirmationDialogController;
  confirmSelectedSuggestedThoughtsDeletion: ConfirmationDialogController;
  confirmSelectedSelectionDeletion: ConfirmationDialogController<DeleteSelectionConfirmation>;
  confirmSelectedThoughtsResetReflections: ConfirmationDialogController;
  confirmSelectedThoughtsConfirmation: ConfirmationDialogController<{
    situationId?: string;
    assignStrategy?: AssignSituationStrategy;
  }>;
  confirmSituationsDeletion: ConfirmationDialogController<RelatedThoughtsDeleteChoice>;
};

const StudioConfirmationContext = createContext<
  StudioConfirmationContextValue | undefined
>(undefined);

export function StudioConfirmationProvider({
  children,
}: {
  children: ReactNode;
}) {
  const confirmSelectedThoughtsDeletion = useConfirmationDialog();
  const confirmSelectedSuggestedThoughtsDeletion = useConfirmationDialog();
  const confirmSelectedSelectionDeletion =
    useConfirmationDialog<DeleteSelectionConfirmation>();
  const confirmSelectedThoughtsResetReflections = useConfirmationDialog();
  const confirmSelectedThoughtsConfirmation = useConfirmationDialog<{
    situationId?: string;
    assignStrategy?: AssignSituationStrategy;
  }>();
  const confirmSituationsDeletion =
    useConfirmationDialog<RelatedThoughtsDeleteChoice>();

  return (
    <StudioConfirmationContext.Provider
      value={{
        confirmSelectedThoughtsDeletion,
        confirmSelectedSuggestedThoughtsDeletion,
        confirmSelectedSelectionDeletion,
        confirmSelectedThoughtsResetReflections,
        confirmSelectedThoughtsConfirmation,
        confirmSituationsDeletion,
      }}
    >
      {children}
    </StudioConfirmationContext.Provider>
  );
}

export function useStudioConfirmationContext() {
  const context = useContext(StudioConfirmationContext);

  if (context === undefined) {
    throw new Error(
      "useStudioConfirmationContext must be used within a StudioConfirmationProvider",
    );
  }

  return context;
}
